import { Group, LoadingOverlay, Stack } from '@mantine/core';
import React, { ReactNode, useState } from 'react';

import { usePortalConfig } from '@portals/api/organizations';
import { ChannelPartnerIcon, CustomerIcon } from '@portals/framework';

import { ClassificationCard } from './ClassificationCard';
import { StepSubtitle, StepTitle } from '../common';
import {
  useClassificationSessionStorage,
  useSignUpWizard,
} from '../wizard/wizard.hooks';
import { SignupWizardContextDataType } from '../wizard/wizard.types';

interface UserTypeItem {
  label: string;
  icon: ReactNode;
  description: string;
  classification: SignupWizardContextDataType['classification'];
}

const USER_TYPE_OPTIONS: UserTypeItem[] = [
  {
    classification: 'customer',
    label: 'Customer',
    icon: <CustomerIcon />,
    description:
      'I’m a customer interested in managing, monitoring and using devices.',
  },
  {
    classification: 'channel_partner',
    label: 'Channel Partner',
    icon: <ChannelPartnerIcon />,
    description:
      "I'm a channel partner involved in the sales, distribution, implementation, monitoring, or supporting of devices on behalf of end-customers.",
  },
];

export function Classification() {
  const { onNext, setContextData } = useSignUpWizard();
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);

  const portalConfig = usePortalConfig();

  const { setClassificationSessionStorageValue } =
    useClassificationSessionStorage();

  const onSelectClassification = (
    classification: SignupWizardContextDataType['classification']
  ) => {
    setContextData((curr) => ({ ...curr, classification }));

    if (classification === 'customer') {
      onNext();
      setClassificationSessionStorageValue('customer');
      return;
    }

    window.open(
      'https://welcome.xyte.io?classification=channel_partner',
      '_self'
    );

    setIsLoaderVisible(true);
  };

  return (
    <Stack maw={1280} align="center">
      <LoadingOverlay visible={isLoaderVisible || portalConfig.isLoading} />

      <Stack w={600}>
        <StepTitle fz={32}>Welcome!</StepTitle>

        <StepSubtitle fz={24}>
          Are you signing up as {portalConfig.data?.name}'s customer or channel
          partner?
        </StepSubtitle>
      </Stack>

      <Group noWrap>
        {USER_TYPE_OPTIONS.map(
          ({ label, icon, description, classification }) => (
            <ClassificationCard
              key={classification}
              icon={icon}
              label={label}
              description={description}
              classification={classification}
              onSelect={onSelectClassification}
            />
          )
        )}
      </Group>
    </Stack>
  );
}
